import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences, moduleSequences } from 'app/pwa/app.cerebral';

import {
  Modal,
  Wrapper,
  Header,
  Stat,
  Statement,
  DidYouKnow,
  CategoryImage,
  Image,
  QuizCard,
  FeedbackFooter,
  InfoPage,
} from './elements';
import { Masonry } from 'dc/Masonry';
import { getTagInfoColors, getTagImage } from 'utils/challenge';
import { useConfig, useText } from 'common/hooks';
import knowledgeItems from 'resources/toolData/tags';

import Quiz from './Quiz';

const deps = {
  tag: state.challenge.tagModal.key,
  showSecondaryInfo: state.challenge.tagModal.showSecondaryInfo,
  showQuiz: state.challenge.tagModal.showQuiz,
  infoType: state.challenge.tagModal.infoType,
  contentTag: state.challenge.tagModal.contentTag,
  quizScore: state.challenge.tagModal.computedUserQuizScore,
  feedbackValue:
    state.challenge.userChallenge.feedback['tags'][state.challenge.tagModal.key]
      .value,
  closeTagModal: moduleSequences.challenge.tagModal.closeTagModalClicked,
  feedbackSubmitted: sequences`challenge.feedbackSubmitted`,
  toggleShowSecondaryInfo:
    moduleSequences.challenge.tagModal.toggleShowSecondaryInfo,
  toggleQuiz: moduleSequences.challenge.tagModal.toggleQuiz,
};

export function TagModal({
  tag,
  showSecondaryInfo,
  showQuiz,
  infoType,
  contentTag,
  quizScore,
  feedbackValue,
  closeTagModal,
  feedbackSubmitted,
  toggleShowSecondaryInfo,
  toggleQuiz,
}: typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const { staticUrl } = useConfig();
  const colors = getTagInfoColors(tag);

  return (
    <Modal onHide={() => closeTagModal()} show>
      <Wrapper>
        <Header
          closeOnClick={() => closeTagModal()}
          color={colors.primary}
          subheader={
            t.has(`challenge.tags.${tag}.subheader`) &&
            t(`challenge.tags.${tag}.subheader`)
          }
        >
          {t(`challenge.tags.${tag}.header`)}
        </Header>
        {showQuiz ? <Quiz /> : null}
        {!showQuiz &&
          (showSecondaryInfo ? (
            <InfoPage
              buttonText={'Back'}
              title={t(
                `challenge.tags.${tag}.${infoType}.${contentTag}.secondary_info.title`
              )}
              text={t(
                `challenge.tags.${tag}.${infoType}.${contentTag}.secondary_info.text`
              )}
              backOnClick={() => toggleShowSecondaryInfo()}
            />
          ) : (
            <>
              <Masonry
                options={{
                  gap: '56px',
                }}
                cards={knowledgeItems[tag]?.map(knowledgeItem => {
                  const {
                    type,
                    hasSecondaryInfo,
                    overlineText,
                    contentTag: itemContentTag,
                    recentlyAdded,
                    imagePath,
                    backgroundColor,
                    href,
                    hrefText,
                  } = knowledgeItem;

                  let bgColor = recentlyAdded ? colors.primary : 'transparent';
                  if (backgroundColor) bgColor = backgroundColor;

                  switch (type) {
                    case 'stat':
                      return (
                        <Stat
                          key={`${tag}-${type}-${itemContentTag}`}
                          className={'grid-item'}
                          subheader={t(
                            `challenge.tags.${tag}.${type}.description`
                          )}
                          onClick={
                            hasSecondaryInfo
                              ? () =>
                                  toggleShowSecondaryInfo({
                                    type,
                                    contentTag: itemContentTag,
                                  })
                              : null
                          }
                          imagePath={`${staticUrl}/${imagePath}`}
                        >
                          {t(`challenge.tags.${tag}.${type}.title`)}
                        </Stat>
                      );
                    case 'quiz':
                      return (
                        <QuizCard
                          key={`${tag}-${type}-${itemContentTag}`}
                          className={'grid-item'}
                          colors={colors}
                          title={t(`challenge.quiz.how_much_you_know.${tag}`)}
                          text={t(`challenge.quiz.common.answer_questions`, {
                            smart_count: quizScore.max,
                          })}
                          quizScore={quizScore}
                          reviewText={t(`challenge.quiz.common.review_answers`)}
                          onClick={() => toggleQuiz()}
                        />
                      );
                    case 'didYouKnow': {
                      const titlePath = `challenge.tags.${tag}.${type}.${itemContentTag}.title`;
                      const textPath = `challenge.tags.${tag}.${type}.${itemContentTag}.text`;

                      return (
                        <DidYouKnow
                          key={`${tag}-${type}-${itemContentTag}`}
                          className={'grid-item'}
                          overlineText={t(overlineText)}
                          title={t(titlePath)}
                          text={t.has(textPath) && t(textPath)}
                          learnMoreText={
                            hasSecondaryInfo
                              ? t(`challenge.tags.learn_more`)
                              : null
                          }
                          onClick={() => {
                            if (!hasSecondaryInfo) return;

                            toggleShowSecondaryInfo({
                              type,
                              contentTag: itemContentTag,
                            });
                          }}
                          recentlyAdded={recentlyAdded}
                          backgroundColor={bgColor}
                          newText={
                            recentlyAdded ? t(`challenge.tags.new`) : null
                          }
                          newColor={
                            recentlyAdded ? colors.primary : 'transparent'
                          }
                          href={href}
                          hrefText={hrefText ? t(hrefText) : null}
                        />
                      );
                    }
                    case 'statement':
                      return (
                        <Statement
                          key={`${tag}-${type}-${itemContentTag}`}
                          className={'grid-item'}
                          colors={colors}
                          imagePath={`${staticUrl}/images/challenge/knowledgeBackground/actionsMatter.png`}
                          onClick={
                            hasSecondaryInfo
                              ? () =>
                                  toggleShowSecondaryInfo({
                                    type,
                                    contentTag: itemContentTag,
                                  })
                              : null
                          }
                        >
                          {t(`challenge.tags.actions_matter`)}
                        </Statement>
                      );
                    case 'categoryImage':
                      return (
                        <CategoryImage
                          key={`${tag}-${type}-categoryImage`}
                          className={'grid-item'}
                          imagePath={getTagImage(staticUrl, tag).large}
                          colors={colors}
                        />
                      );
                    case 'image':
                      return (
                        <Image
                          key={`${tag}-${type}-${imagePath}`}
                          className={'grid-item'}
                          src={`${staticUrl}/${imagePath}`}
                        />
                      );
                    default:
                      return null;
                  }
                })}
              />
              <FeedbackFooter
                text={t('challenge.tags.useful')}
                onClick={value =>
                  feedbackSubmitted({ type: 'tags', category: tag, value })
                }
                selected={feedbackValue ? Number(feedbackValue) : 0}
              />
            </>
          ))}
      </Wrapper>
    </Modal>
  );
}

export default connect(deps, TagModal);
